import { useContext, useRef, useState } from "react";
import { toast } from "react-toastify";
import { GlobalContext } from "../../Context/GlobalContext";

const normalizeProductionItem = (item = {}) => ({
  item_id: String(item?.item_id ?? ""),
  item_name: String(item?.item_name ?? ""),
  item_production_quantity_id: String(
    item?.item_production_quantity_id ?? "",
  ),
  quantity_per_person: String(item?.quantity_per_person ?? ""),
  unit_type: String(item?.unit_type ?? ""),
  status: String(item?.status ?? ""),
  is_dummy: String(item?.is_dummy ?? ""),
  notes: String(item?.notes ?? ""),
  created: String(item?.created ?? ""),
  updated_at: String(item?.updated_at ?? ""),
});

const normalizeUnitType = (unit, index = 0) => {
  if (typeof unit === "string" || typeof unit === "number") {
    const value = String(unit);
    return { id: value || String(index), value, label: value };
  }

  const value = String(
    unit?.unit_type ??
      unit?.unitType ??
      unit?.value ??
      unit?.code ??
      unit?.unit_name ??
      unit?.name ??
      "",
  );

  return {
    id: String(
      unit?.id ??
        unit?.unit_type_id ??
        unit?.pk_unit_type_id ??
        value ??
        index,
    ),
    value,
    label: String(
      unit?.label ?? unit?.display_name ?? unit?.unit_name ?? unit?.name ?? value,
    ),
  };
};

const getResponseRows = (data) => {
  if (Array.isArray(data)) return data;

  const candidates = [
    data?.response,
    data?.data,
    data?.result,
    data?.items,
    data?.itemList,
    data?.unitTypes,
    data?.productionUnitTypes,
    data?.response?.data,
    data?.response?.result,
    data?.response?.items,
    data?.response?.itemList,
    data?.response?.unitTypes,
    data?.response?.productionUnitTypes,
    data?.data?.response,
    data?.data?.result,
    data?.data?.items,
    data?.data?.itemList,
    data?.data?.unitTypes,
    data?.data?.productionUnitTypes,
  ];

  return candidates.find(Array.isArray) || [];
};

const isReadSuccess = (data, rows = []) => {
  if (Array.isArray(data) || rows.length > 0) return true;

  const responseCode = String(
    data?.responseCode ?? data?.response_code ?? "",
  ).toLowerCase();

  if (["00", "0", "200", "success", "true"].includes(responseCode)) {
    return true;
  }

  const message = String(
    data?.responseMessage ?? data?.message ?? data?.msg ?? "",
  ).toLowerCase();

  if (message.includes("fail") || message.includes("error")) return false;

  return String(data?.status ?? "") === "1" || data?.success === true;
};

const isUpdateSuccess = (data) => {
  const responseCode = String(
    data?.responseCode ?? data?.response_code ?? "",
  ).toLowerCase();

  if (["00", "0", "200", "success", "true"].includes(responseCode)) {
    return true;
  }

  if (data?.success === true) return true;

  const nestedResult = Array.isArray(data?.result) ? data.result[0] : data?.result;
  const resultFlag = String(
    nestedResult?.result ?? data?.resultFlag ?? data?.statusFlag ?? "",
  ).toUpperCase();

  return ["Y", "YES", "SUCCESS", "TRUE"].includes(resultFlag);
};

export default function useBhandarewala() {
  const { api } = useContext(GlobalContext);

  const itemsFetchPromiseRef = useRef(null);
  const unitTypesFetchPromiseRef = useRef(null);
  const updatePromiseRef = useRef(new Map());

  const [bhandareProductionItems, setBhandareProductionItems] = useState([]);
  const [bhandareProductionItemsLoading, setBhandareProductionItemsLoading] =
    useState(false);
  const [productionUnitTypes, setProductionUnitTypes] = useState([]);
  const [productionUnitTypesLoading, setProductionUnitTypesLoading] =
    useState(false);
  const [bhandareProductionUpdating, setBhandareProductionUpdating] = useState(
    {},
  );

  // POST /Order/getItemBhandarewala
  const getItemBhandarewala = async () => {
    if (itemsFetchPromiseRef.current) {
      return itemsFetchPromiseRef.current;
    }

    const requestPromise = (async () => {
      try {
        if (!api) {
          toast.error("Main Anlook API instance missing");
          return [];
        }

        setBhandareProductionItemsLoading(true);

        const res = await api.post("/Order/getItemBhandarewala", "", {
          headers: { accept: "*/*" },
        });

        console.log("GET ITEM BHANDAREWALA RESPONSE:", res?.data);

        const rows = getResponseRows(res?.data);
        const finalData = rows.map(normalizeProductionItem);

        if (isReadSuccess(res?.data, finalData)) {
          setBhandareProductionItems(finalData);
          return finalData;
        }

        setBhandareProductionItems([]);
        toast.error(
          res?.data?.responseMessage || "Failed to fetch production quantities",
        );
        return [];
      } catch (err) {
        console.error(
          "Get Bhandarewala production items error:",
          err?.response?.data || err,
        );
        setBhandareProductionItems([]);
        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error fetching production quantities",
        );
        return [];
      } finally {
        setBhandareProductionItemsLoading(false);
      }
    })();

    itemsFetchPromiseRef.current = requestPromise;

    try {
      return await requestPromise;
    } finally {
      if (itemsFetchPromiseRef.current === requestPromise) {
        itemsFetchPromiseRef.current = null;
      }
    }
  };

  // POST /Order/getProductionUnitTypes
  const getProductionUnitTypes = async () => {
    if (unitTypesFetchPromiseRef.current) {
      return unitTypesFetchPromiseRef.current;
    }

    const requestPromise = (async () => {
      try {
        if (!api) {
          toast.error("Main Anlook API instance missing");
          return [];
        }

        setProductionUnitTypesLoading(true);

        const res = await api.post("/Order/getProductionUnitTypes", "", {
          headers: { accept: "*/*" },
        });

        console.log("GET PRODUCTION UNIT TYPES RESPONSE:", res?.data);

        const rows = getResponseRows(res?.data);
        const finalData = rows
          .map(normalizeUnitType)
          .filter((unit) => String(unit?.value || "").trim());

        if (isReadSuccess(res?.data, finalData)) {

          setProductionUnitTypes(finalData);
          return finalData;
        }

        setProductionUnitTypes([]);
        toast.error(
          res?.data?.responseMessage || "Failed to fetch production unit types",
        );
        return [];
      } catch (err) {
        console.error(
          "Get production unit types error:",
          err?.response?.data || err,
        );
        setProductionUnitTypes([]);
        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error fetching production unit types",
        );
        return [];
      } finally {
        setProductionUnitTypesLoading(false);
      }
    })();

    unitTypesFetchPromiseRef.current = requestPromise;

    try {
      return await requestPromise;
    } finally {
      if (unitTypesFetchPromiseRef.current === requestPromise) {
        unitTypesFetchPromiseRef.current = null;
      }
    }
  };

  // POST /Order/updateItemBhandarewla
  const updateItemBhandarewla = async (payload = {}) => {
    const requestPayload = {
      item_production_quantity_id: String(
        payload?.item_production_quantity_id ?? "",
      ).trim(),
      quantity_per_person: String(payload?.quantity_per_person ?? "").trim(),
      unit_type: String(payload?.unit_type ?? "").trim(),
    };

    if (!requestPayload.item_production_quantity_id) {
      toast.error("Production quantity ID is missing");
      return false;
    }

    if (
      requestPayload.quantity_per_person === "" ||
      !Number.isFinite(Number(requestPayload.quantity_per_person)) ||
      Number(requestPayload.quantity_per_person) < 0
    ) {
      toast.error("Please enter a valid quantity per person");
      return false;
    }

    if (!requestPayload.unit_type) {
      toast.error("Please select unit type");
      return false;
    }

    const rowId = requestPayload.item_production_quantity_id;
    const existingPromise = updatePromiseRef.current.get(rowId);

    if (existingPromise) {
      return existingPromise;
    }

    const requestPromise = (async () => {
      try {
        setBhandareProductionUpdating((prev) => ({
          ...prev,
          [rowId]: true,
        }));

        console.log("UPDATE ITEM BHANDAREWALA PAYLOAD:", requestPayload);

        const res = await api.post(
          "/Order/updateItemBhandarewla",
          requestPayload,
          {
            headers: {
              accept: "*/*",
              "Content-Type": "application/json-patch+json",
            },
          },
        );

        console.log("UPDATE ITEM BHANDAREWALA RESPONSE:", res?.data);

        if (isUpdateSuccess(res?.data)) {
          toast.success(
            res?.data?.responseMessage || "Production quantity updated",
          );

          await getItemBhandarewala();
          return true;
        }

        toast.error(
          res?.data?.responseMessage || "Failed to update production quantity",
        );
        return false;
      } catch (err) {
        console.error(
          "Update Bhandarewala production item error:",
          err?.response?.data || err,
        );
        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error updating production quantity",
        );
        return false;
      } finally {
        setBhandareProductionUpdating((prev) => ({
          ...prev,
          [rowId]: false,
        }));
      }
    })();

    updatePromiseRef.current.set(rowId, requestPromise);

    try {
      return await requestPromise;
    } finally {
      if (updatePromiseRef.current.get(rowId) === requestPromise) {
        updatePromiseRef.current.delete(rowId);
      }
    }
  };

  const refreshBhandarewalaProduction = async () => {
    const [items, units] = await Promise.all([
      getItemBhandarewala(),
      getProductionUnitTypes(),
    ]);

    return { items, units };
  };

  return {
    bhandareProductionItems,
    bhandareProductionItemsLoading,
    productionUnitTypes,
    productionUnitTypesLoading,
    bhandareProductionUpdating,
    getItemBhandarewala,
    getProductionUnitTypes,
    updateItemBhandarewla,
    refreshBhandarewalaProduction,
  };
}
